import { Box, Stack, Typography } from '@mui/material';

import { Icon } from '@karehero/llama/components/atoms/Icon/Icon';
import { radius } from '@karehero/llama/theme/radius';

import TaskRow from '@/north-star/TaskRow';
import { welcomeHeadingSx } from '@/north-star/theme';
import { HOME } from '@/north-star/NorthStar.content';
import {
  CLAIM_TOTAL_ANNUAL,
  NEXT_TASKS,
} from '@/north-star/NorthStar.fixtures';

// Home tab content (the shell owns the status bar and nav). The "you could
// claim" hero, then a short preview of the tasks (the Attendance Allowance claim
// leads the list); the full list lives in the Tasks tab.
const PREVIEW_COUNT = 2;

export type NorthStarHomeProps = {
  onOpenTask: (id: string) => void;
  onSeeBreakdown: () => void;
  doneIds: Set<string>;
};

const NorthStarHome = ({
  onOpenTask,
  onSeeBreakdown,
  doneIds,
}: NorthStarHomeProps) => (
  <Stack spacing={3}>
    <Stack spacing={0.5}>
      <Typography variant='h2' sx={welcomeHeadingSx}>
        {HOME.greeting}
      </Typography>
      <Typography variant='body1' color='text.secondary'>
        {HOME.subtitle}
      </Typography>
    </Stack>

    {/* funding found — tap the card for the full breakdown */}
    <Stack
      component='button'
      onClick={onSeeBreakdown}
      direction='row'
      spacing={2}
      sx={{
        alignItems: 'center',
        justifyContent: 'space-between',
        p: 3,
        bgcolor: 'custom.coral.50',
        border: 1,
        borderColor: 'custom.coral.100',
        borderRadius: `${radius.xl}px`,
        color: 'custom.coral.700',
        cursor: 'pointer',
        textAlign: 'left',
        width: '100%',
      }}
    >
      <Box sx={{ minWidth: 0 }}>
        <Typography variant='body2' color='custom.coral.700'>
          {HOME.fundingTitle}
        </Typography>
        <Typography variant='h1' color='custom.coral.700'>
          {CLAIM_TOTAL_ANNUAL}
          <Typography component='span' variant='body1' color='custom.coral.700'>
            {' '}
            {HOME.fundingPerYear}
          </Typography>
        </Typography>
        <Typography variant='caption' color='text.secondary'>
          {HOME.fundingSub}
        </Typography>
      </Box>
      <Box sx={{ display: 'flex', flexShrink: 0 }}>
        <Icon icon={'chevron-right' as never} fontSize={16} />
      </Box>
    </Stack>

    {/* tasks preview — full list lives in the Tasks tab */}
    <Box>
      <Typography variant='h4'>{HOME.tasksTitle}</Typography>
      <Stack sx={{ mt: 2 }} spacing={1.5}>
        {NEXT_TASKS.slice(0, PREVIEW_COUNT).map((t) => (
          <TaskRow
            key={t.id}
            task={t}
            done={doneIds.has(t.id)}
            onClick={() => onOpenTask(t.id)}
          />
        ))}
      </Stack>
    </Box>
  </Stack>
);

NorthStarHome.displayName = 'NorthStarHome';
export default NorthStarHome;
